// ErrorBoundary — where a render-time throw is caught instead of white-screening
// the app. React only exposes error catching via a class component (no hook
// equivalent), so this is a sanctioned class living in the impure mount shell;
// it holds no domain logic, only "did this subtree throw -> show a recoverable
// surface".
//
// Two placements (audit D2):
//   • ROOT (no `fallback`) — the last-resort backstop at the app mount. Recovery
//     is a full reload: a throw that escaped every local boundary means the tree
//     is suspect, so a reload rebuilds from the server-truth session.
//   • PER-PANE (`fallback` given) — wraps each pane's body with a LOCAL
//     recoverable surface (Retry / Close), so a throw in ONE view fails in that
//     view and the rest of the app — chrome, other panes, unsynced edits — stays
//     alive. `resetKeys` clears a stale crash when the pane navigates elsewhere
//     (a changed key means the crashing subtree is gone), without remounting the
//     healthy children on every render.

import { Trans } from "@lingui/react/macro";
import { Component, type ErrorInfo, type ReactNode } from "react";

type Props = {
    readonly children: ReactNode;
    // A LOCAL fallback (per-pane, D2). Receives `retry`, which clears the crash
    // in place — a transient render glitch recovers without a full reload. When
    // absent, the full-page reload fallback renders (the root boundary).
    readonly fallback?: (retry: () => void) => ReactNode;
    // When any key changes while crashed, the boundary auto-resets — used to
    // clear a crash the moment the pane navigates away from the culprit view.
    readonly resetKeys?: readonly unknown[];
};
type State = { readonly crashed: boolean };

// The auto-reset decision (exported for test): while crashed, the boundary
// clears itself the moment its reset keys change — a navigation away from the
// culprit view. `undefined`-vs-defined counts as a change; equal-length arrays
// compare per element by identity.
export const keysChanged = (
    a: readonly unknown[] | undefined,
    b: readonly unknown[] | undefined,
): boolean => {
    if (a === undefined || b === undefined) return a !== b;
    if (a.length !== b.length) return true;
    return a.some((v, i) => !Object.is(v, b[i]));
};

export class ErrorBoundary extends Component<Props, State> {
    state: State = { crashed: false };

    static getDerivedStateFromError(): State {
        return { crashed: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        // Telemetry seam: the detail stays in the console (developer
        // surface), never on the user-facing fallback.
        console.error("Unhandled render error:", error, info.componentStack);
    }

    componentDidUpdate(prev: Props): void {
        // A navigation away from the crashing subtree (resetKeys changed) clears
        // the local crash so the pane is usable again without a reload.
        if (
            this.state.crashed &&
            keysChanged(prev.resetKeys, this.props.resetKeys)
        )
            this.setState({ crashed: false });
    }

    render(): ReactNode {
        if (!this.state.crashed) return this.props.children;
        if (this.props.fallback !== undefined)
            return this.props.fallback(() => this.setState({ crashed: false }));
        return (
            <div style={wrapStyle}>
                <strong style={titleStyle}>
                    <Trans>Something went wrong</Trans>
                </strong>
                <p style={bodyStyle}>
                    <Trans>
                        The app hit an unexpected error. Reloading usually
                        clears it.
                    </Trans>
                </p>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={buttonStyle}
                >
                    <Trans>Reload</Trans>
                </button>
            </div>
        );
    }
}

const wrapStyle: React.CSSProperties = {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-3)",
    padding: "var(--space-6)",
    textAlign: "center",
    color: "var(--color-fg)",
    background: "var(--color-bg-subtle)",
};

const titleStyle: React.CSSProperties = {
    fontSize: "var(--text-lg)",
    fontWeight: "var(--weight-semibold)",
};

const bodyStyle: React.CSSProperties = {
    margin: 0,
    maxWidth: "28rem",
    fontSize: "var(--text-sm)",
    color: "var(--color-fg-dim)",
};

const buttonStyle: React.CSSProperties = {
    padding: "var(--space-2) var(--space-4)",
    borderRadius: "var(--radius-sm)",
    border: "none",
    cursor: "pointer",
    background: "var(--color-accent)",
    color: "var(--color-accent-fg)",
    fontSize: "var(--text-sm)",
};
